// Catch-all / "Coming Soon" page (issue #137).
//
// Several nav items are flagged `comingSoon` in Sidebar.js (disabled in the
// nav) but had no matching <Route>, so a direct hit / shared / bookmarked
// URL rendered the app chrome with a blank main area. This component is
// mapped both to those specific paths (mode="coming-soon") and to a trailing
// path="*" 404 so any unknown URL shows an explanatory page instead of
// silent blank content.

import React from 'react';
import { useLocation, Link as RouterLink } from 'react-router-dom';
import { Box, Paper, Typography, Button } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import ConstructionIcon from '@mui/icons-material/Construction';
import SearchOffIcon from '@mui/icons-material/SearchOff';

// Pretty labels for the known coming-soon paths (kept in sync with the
// `comingSoon: true` items in Sidebar.js).
const COMING_SOON_LABELS = {
  '/senate-motions': 'Senate Notices of Motion',
  '/committee-hansard': 'Committee Hansard',
  '/press-releases': 'Press Releases',
  '/newspaper-clippings': 'Newspaper Clippings',
  '/radio-tv': 'Radio and TV',
  '/library-publications': 'Library Publications',
  '/parliamentary-papers': 'Parliamentary Papers',
  '/budget-papers': 'Budget Papers',
  '/education': 'Education',
  '/statute-of-westminster': 'Statute of Westminster',
  '/federal-conventions': '1890s Federal Conventions',
};

export default function NotFound({ mode }) {
  const location = useLocation();
  const knownLabel = COMING_SOON_LABELS[location.pathname];
  const comingSoon = mode === 'coming-soon' || Boolean(knownLabel);

  return (
    <Box sx={{ maxWidth: 640, mx: 'auto', px: 2, py: 8, textAlign: 'center' }}>
      <Paper elevation={3} sx={{ p: { xs: 3, sm: 5 } }}>
        {comingSoon ? (
          <ConstructionIcon sx={{ fontSize: 56, color: 'primary.main', mb: 2 }} />
        ) : (
          <SearchOffIcon sx={{ fontSize: 56, color: 'text.secondary', mb: 2 }} />
        )}

        <Typography component="h1" variant="h4" gutterBottom>
          {comingSoon ? 'Coming soon' : 'Page not found'}
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
          {comingSoon ? (
            <>
              {knownLabel ? <strong>{knownLabel}</strong> : 'This dataset'} is
              not available yet — we're working on bringing it to ParlSearch.
              Check back soon.
            </>
          ) : (
            <>
              We couldn't find <code>{location.pathname}</code>. The page may
              have moved, or the link may be incorrect.
            </>
          )}
        </Typography>

        <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button
            component={RouterLink}
            to="/"
            variant="contained"
            startIcon={<HomeIcon />}
          >
            Back to home
          </Button>
          <Button component={RouterLink} to="/help" variant="outlined">
            Browse available data
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
